import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button, SwitchWithLabel, Tooltip, toast } from "@acme/ui";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { apiKeyModalAtom, teamActiveAtom, userAtom, upgradeModalAtom } from "../store/user";
import { apiKeyAnthropicAtom, apiKeyAtom, apiKeyExistAtom, apiKeyGoogleAtom, apiKeyXaiAtom, apiKeyDeepseekAtom, apiKeyGlmAtom, apiKeyKimiAtom, apiKeyQwenAtom, type ApiKeyProvider } from "../store/setting";
import { generateNanoId } from "@acme/util";
import { chatApplySavedKeyAtom } from "../store/chat";
import { api } from "@acme/client";
import { isLoadAPIKeyAtom } from "../store/util";
import { useUserSubscription } from "../hooks/useSubscription";

// Provider configuration with display info, URLs, and brand colors
const PROVIDER_CONFIG: Record<ApiKeyProvider, {
  name: string;
  url: string;
  placeholder: string;
  description: string;
  brandColor: string;
  bgGradient: string;
  iconBg: string;
  features: string[];
}> = {
  openai: {
    name: 'OpenAI',
    url: 'https://platform.openai.com/api-keys',
    placeholder: 'sk-proj-...',
    description: 'Access GPT-6.1 Terra, GPT-6 Sol and Luna models',
    brandColor: 'text-emerald-600 dark:text-emerald-400',
    bgGradient: 'from-emerald-500/20 via-teal-500/10 to-cyan-500/5',
    iconBg: 'bg-emerald-500',
    features: ['GPT-6.1 Terra', '1M Context', 'Vision & Tools', 'Adjustable Reasoning'],
  },
  anthropic: {
    name: 'Anthropic',
    url: 'https://console.anthropic.com/settings/keys',
    placeholder: 'sk-ant-api...',
    description: 'Access Claude Opus 5.5, Sonnet 5.5 and Haiku 5.5',
    brandColor: 'text-orange-600 dark:text-orange-400',
    bgGradient: 'from-orange-500/20 via-amber-500/10 to-yellow-500/5',
    iconBg: 'bg-orange-500',
    features: ['Claude Opus 5.5', '1M Context', 'Adaptive Thinking', 'Top-tier Coding'],
  },
  google: {
    name: 'Google AI',
    url: 'https://aistudio.google.com/apikey',
    placeholder: 'AIzaSy...',
    description: 'Access Gemini 4 Argon and Gemini 3.8 Flash',
    brandColor: 'text-blue-600 dark:text-blue-400',
    bgGradient: 'from-blue-500/20 via-indigo-500/10 to-violet-500/5',
    iconBg: 'bg-blue-500',
    features: ['Gemini 4 Argon', '1M Context', 'Multimodal', 'Fast Flash Models'],
  },
  xai: {
    name: 'xAI',
    url: 'https://console.x.ai/',
    placeholder: 'xai-...',
    description: 'Access Grok 4.7 and Grok 4.5 models',
    brandColor: 'text-slate-700 dark:text-slate-200',
    bgGradient: 'from-slate-500/20 via-gray-500/10 to-zinc-500/5',
    iconBg: 'bg-slate-700 dark:bg-slate-500',
    features: ['Grok 4.7', '500K Context', 'Reasoning Effort', 'Agentic Tools'],
  },
  deepseek: {
    name: 'DeepSeek',
    url: 'https://platform.deepseek.com/api_keys',
    placeholder: 'sk-...',
    description: 'Access DeepSeek V4 Pro and V4 Flash models',
    brandColor: 'text-purple-600 dark:text-purple-400',
    bgGradient: 'from-purple-500/20 via-violet-500/10 to-fuchsia-500/5',
    iconBg: 'bg-purple-500',
    features: ['DeepSeek V4 Pro', 'Thinking Mode', 'Very Low Cost', 'Open Weights'],
  },
  glm: {
    name: 'Z.AI (GLM)',
    url: 'https://z.ai/manage-apikey/apikey-list',
    placeholder: '...',
    description: 'Access GLM-5.2, GLM-5 and GLM-4.7 models',
    brandColor: 'text-indigo-600 dark:text-indigo-400',
    bgGradient: 'from-indigo-500/20 via-blue-500/10 to-sky-500/5',
    iconBg: 'bg-indigo-500',
    features: ['GLM-5.2', '1M Context', 'Thinking Mode', 'Strong Coding'],
  },
  kimi: {
    name: 'Moonshot (Kimi)',
    url: 'https://platform.moonshot.ai/console/api-keys',
    placeholder: 'sk-...',
    description: 'Access Kimi K3 and K2.7 Code models',
    brandColor: 'text-fuchsia-600 dark:text-fuchsia-400',
    bgGradient: 'from-fuchsia-500/20 via-purple-500/10 to-violet-500/5',
    iconBg: 'bg-fuchsia-500',
    features: ['Kimi K3', '1M Context', 'Native Vision', 'Always Reasoning'],
  },
  qwen: {
    name: 'Alibaba (Qwen)',
    url: 'https://modelstudio.console.alibabacloud.com/',
    placeholder: 'sk-...',
    description: 'Access Qwen3.7 Max, Plus and Flash models',
    brandColor: 'text-rose-600 dark:text-rose-400',
    bgGradient: 'from-rose-500/20 via-pink-500/10 to-orange-500/5',
    iconBg: 'bg-rose-500',
    features: ['Qwen3.7 Max', 'Long Context', 'Multimodal', 'Cost Effective'],
  },
};

// Provider icons
const ProviderIcon: React.FC<{ provider: ApiKeyProvider; className?: string }> = ({ provider, className = '' }) => {
  const icons: Record<ApiKeyProvider, React.ReactNode> = {
    openai: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" />
      </svg>
    ),
    anthropic: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.304 3.541h-3.672l6.696 16.918h3.672l-6.696-16.918zm-10.608 0L0 20.459h3.744l1.368-3.6h6.624l1.368 3.6h3.744L10.152 3.541H6.696zm.456 10.8l2.304-6.072 2.304 6.072H7.152z" />
      </svg>
    ),
    google: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
      </svg>
    ),
    xai: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
      </svg>
    ),
    deepseek: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="12" r="10" strokeWidth="2" stroke="currentColor" fill="none" />
        <path d="M12 6v12M6 12h12" strokeWidth="2" stroke="currentColor" strokeLinecap="round" />
      </svg>
    ),
    glm: (
      <svg className={className} viewBox="0 0 24 24" fill="none">
        <path d="M12 2.5 21 7v10l-9 4.5L3 17V7l9-4.5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M12 8l4.5 2.4v4.8L12 17.6 7.5 15.2v-4.8L12 8Z" fill="currentColor" />
      </svg>
    ),
    kimi: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.5 15.6A8.4 8.4 0 0 1 9 4a9 9 0 1 0 11.5 11.6Z" />
      </svg>
    ),
    qwen: (
      <svg className={className} viewBox="0 0 24 24" fill="none">
        <path d="M4.5 7.8 12 3.5l7.5 4.3v8.4L12 20.5l-7.5-4.3V7.8Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M12 9v6.8M8.4 10.8v3.2M15.6 10.8v3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  };
  return <>{icons[provider]}</>;
};

// Mask API key for display
const maskApiKey = (key: string): string => {
  if (!key || key.length < 10) return key || '';
  return key.slice(0, 6) + '•'.repeat(Math.min(key.length - 10, 16)) + key.slice(-4);
};

/** Provider order for the switcher row. */
const PROVIDER_TABS: ApiKeyProvider[] = ['openai', 'anthropic', 'google', 'xai', 'deepseek', 'glm', 'kimi', 'qwen'];

/**
 * Guess the provider from a pasted key so a user who opened the OpenAI prompt
 * but pasted a Claude key doesn't save it in the wrong slot. Only prefixes that
 * are unique to one provider are matched; `sk-...` is shared by several.
 */
export const detectKeyProvider = (key: string): ApiKeyProvider | null => {
  const k = key.trim();
  if (k.startsWith('sk-ant-')) return 'anthropic';
  if (k.startsWith('AIza')) return 'google';
  if (k.startsWith('xai-')) return 'xai';
  if (k.startsWith('sk-proj-') || k.startsWith('sk-svcacct-')) return 'openai';
  return null;
};

export const ApiKeyModal: React.FC = () => {
  const [_apiKey, setApiKey] = useAtom(apiKeyAtom);
  const [_apiKeyAnthropic, setApiKeyAnthropic] = useAtom(apiKeyAnthropicAtom);
  const [_apiKeyGoogle, setApiKeyGoogle] = useAtom(apiKeyGoogleAtom);
  const [_apiKeyXai, setApiKeyXai] = useAtom(apiKeyXaiAtom);
  const [_apiKeyDeepseek, setApiKeyDeepseek] = useAtom(apiKeyDeepseekAtom);
  const [_apiKeyGlm, setApiKeyGlm] = useAtom(apiKeyGlmAtom);
  const [_apiKeyKimi, setApiKeyKimi] = useAtom(apiKeyKimiAtom);
  const [_apiKeyQwen, setApiKeyQwen] = useAtom(apiKeyQwenAtom);
  const [apiKeyModal, setApiKeyModal] = useAtom(apiKeyModalAtom);
  const applySavedKey = useSetAtom(chatApplySavedKeyAtom);

  const onClose = () => {
    setApiKeyModal(false);
  };

  // Determine the provider type from the modal state
  const providerType: ApiKeyProvider = useMemo(() => {
    if (apiKeyModal === 'anthropic') return 'anthropic';
    if (apiKeyModal === 'google') return 'google';
    if (apiKeyModal === 'xai') return 'xai';
    if (apiKeyModal === 'deepseek') return 'deepseek';
    if (apiKeyModal === 'glm') return 'glm';
    if (apiKeyModal === 'kimi') return 'kimi';
    if (apiKeyModal === 'qwen') return 'qwen';
    return 'openai';
  }, [apiKeyModal]);

  const handleSave = (newApiKey: string) => {
    const keyData = { key: newApiKey, id: generateNanoId(16), created: new Date() };

    switch (providerType) {
      case 'anthropic':
        setApiKeyAnthropic(keyData);
        break;
      case 'google':
        setApiKeyGoogle(keyData);
        break;
      case 'xai':
        setApiKeyXai(keyData);
        break;
      case 'deepseek':
        setApiKeyDeepseek(keyData);
        break;
      case 'glm':
        setApiKeyGlm(keyData);
        break;
      case 'kimi':
        setApiKeyKimi(keyData);
        break;
      case 'qwen':
        setApiKeyQwen(keyData);
        break;
      default:
        setApiKey(keyData);
    }
    // Close first: applySavedKey may open the prompt again for a second key.
    onClose();
    applySavedKey(providerType);
  };

  const handleRemove = () => {
    switch (providerType) {
      case 'anthropic':
        setApiKeyAnthropic(null);
        break;
      case 'google':
        setApiKeyGoogle(null);
        break;
      case 'xai':
        setApiKeyXai(null);
        break;
      case 'deepseek':
        setApiKeyDeepseek(null);
        break;
      case 'glm':
        setApiKeyGlm(null);
        break;
      case 'kimi':
        setApiKeyKimi(null);
        break;
      case 'qwen':
        setApiKeyQwen(null);
        break;
      default:
        setApiKey(null);
    }
  };

  if (!apiKeyModal) return null;
  return (
    <ApiKeyInputModal
      type={providerType}
      isOpen={true}
      onClose={onClose}
      onSave={handleSave}
      onRemove={handleRemove}
      onSwitchProvider={(provider) => setApiKeyModal(provider)}
    />
  );
};

type UpgradeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (apiKey: string) => void;
  onRemove: () => void;
  onSwitchProvider?: (provider: ApiKeyProvider) => void;
  type?: ApiKeyProvider;
};

const ApiKeyInputModal = ({ isOpen, onClose, onSave, onRemove, onSwitchProvider, type = 'openai' }: UpgradeModalProps) => {
  const [isAddApiKeyCloud, setIsAddApiKeyCloud] = useState(false);
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);

  const { isAllowCloud } = useUserSubscription() as { isAllowCloud: { allow: boolean; allowApiKeyStorage: boolean; message: string; maxSyncChats?: number; maxSyncNotes?: number } };
  const setUpgradeModal = useSetAtom(upgradeModalAtom);

  const apiKeyOpenAIState = useAtomValue(apiKeyAtom);
  const apiKeyAnthropicState = useAtomValue(apiKeyAnthropicAtom);
  const apiKeyGoogleState = useAtomValue(apiKeyGoogleAtom);
  const apiKeyXaiState = useAtomValue(apiKeyXaiAtom);
  const apiKeyDeepseekState = useAtomValue(apiKeyDeepseekAtom);
  const apiKeyGlmState = useAtomValue(apiKeyGlmAtom);
  const apiKeyKimiState = useAtomValue(apiKeyKimiAtom);
  const apiKeyQwenState = useAtomValue(apiKeyQwenAtom);

  const user = useAtomValue(userAtom);
  const teamActive = useAtomValue(teamActiveAtom);
  const [apiKey, setApiKey] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [apiKeyExist, setApiKeyExist] = useAtom(apiKeyExistAtom);
  const setIsLoadAPIKey = useSetAtom(isLoadAPIKeyAtom);

  const providerConfig = PROVIDER_CONFIG[type];

  const configuredProviders: Record<ApiKeyProvider, boolean> = {
    openai: !!apiKeyOpenAIState?.key,
    anthropic: !!apiKeyAnthropicState?.key,
    google: !!apiKeyGoogleState?.key,
    xai: !!apiKeyXaiState?.key,
    deepseek: !!apiKeyDeepseekState?.key,
    glm: !!apiKeyGlmState?.key,
    kimi: !!apiKeyKimiState?.key,
    qwen: !!apiKeyQwenState?.key,
  };

  const apiKeyState = useMemo(() => {
    switch (type) {
      case 'anthropic': return apiKeyAnthropicState;
      case 'google': return apiKeyGoogleState;
      case 'xai': return apiKeyXaiState;
      case 'deepseek': return apiKeyDeepseekState;
      case 'glm': return apiKeyGlmState;
      case 'kimi': return apiKeyKimiState;
      case 'qwen': return apiKeyQwenState;
      default: return apiKeyOpenAIState;
    }
  }, [apiKeyOpenAIState, apiKeyAnthropicState, apiKeyGoogleState, apiKeyXaiState, apiKeyDeepseekState, apiKeyGlmState, apiKeyKimiState, apiKeyQwenState, type]);

  const { data: dataCheck, isLoading: isLoadingCheck } = api.user.teamHasAPIKey.useQuery(
    { teamId: teamActive?.id || '' },
    { enabled: !!user?.id && !!teamActive?.id }
  );
  const { mutateAsync: createAPIKey, isLoading: isLoadingAdd } = api.user.updateTeamAPIKey.useMutation();
  const { mutateAsync: removeApiKeyCloud, isLoading: isLoadingRemove } = api.user.removeApiKey.useMutation({
    onSuccess: () => setApiKeyExist(false),
    onError: () => setApiKeyExist(false)
  });

  const handleSave = async () => {
    if (!apiKey.trim()) return;

    // Block cloud sync if user doesn't have access
    if ((dataCheck || user?.id) && isAddApiKeyCloud) {
      if (!isAllowCloud.allowApiKeyStorage) {
        toast.error(isAllowCloud.message);
        setUpgradeModal({ isOpen: true, lastOpen: new Date() });
        return;
      }
      await createAPIKey({ teamId: teamActive?.id || '', apiKey, type });
      setApiKeyExist(true);
    }
    if (apiKeyExist && !isAddApiKeyCloud) {
      await removeApiKeyCloud({ teamId: teamActive?.id || '', type });
    }
    onSave(apiKey);
    setIsEditMode(false);
  };

  const handleRemove = async () => {
    if (apiKeyExist) {
      await removeApiKeyCloud({ teamId: teamActive?.id || '', type });
    }
    onRemove();
    setShowConfirmRemove(false);
    onClose();
  };

  // A key typed for another provider: remember it and switch tabs. The effect
  // below that syncs `apiKey` from the stored key would otherwise clear it.
  const pendingKeyRef = React.useRef<string | null>(null);

  const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const detected = detectKeyProvider(value);
    if (detected && detected !== type && onSwitchProvider) {
      pendingKeyRef.current = value;
      onSwitchProvider(detected);
      toast.success(`Looks like a ${PROVIDER_CONFIG[detected].name} key. Switched to ${PROVIDER_CONFIG[detected].name}.`);
      return;
    }
    setApiKey(value);
  };

  const isHasCloudAPI = useMemo(() => !!user?.id && !!teamActive?.id && dataCheck, [user?.id, teamActive?.id, dataCheck]);
  const isLoadingAPIKey = useMemo(() => isLoadingCheck && user?.id && teamActive?.id, [isLoadingCheck, user?.id, teamActive?.id]);
  const hasExistingKey = !!apiKeyState?.key;

  useEffect(() => {
    if (pendingKeyRef.current) {
      setApiKey(pendingKeyRef.current);
      pendingKeyRef.current = null;
      setIsEditMode(true);
    } else if (apiKeyState?.key) {
      setApiKey(apiKeyState.key);
    } else {
      setApiKey("");
      setIsEditMode(true);
    }
  }, [apiKeyState, type]);

  useEffect(() => {
    if (isOpen && !hasExistingKey) {
      setIsEditMode(true);
    }
  }, [isOpen, hasExistingKey]);

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100001] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md relative overflow-hidden border border-gray-200/50 dark:border-gray-700/50"
            initial={{ scale: 0.9, opacity: 0, y: 30 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 30 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Provider switcher: users can add whichever key they have */}
            {onSwitchProvider && (
              <div className="flex gap-1 overflow-x-auto px-3 pt-3 pb-2 border-b border-gray-100 dark:border-gray-800 scrollbar-none" role="tablist" aria-label="AI provider">
                {PROVIDER_TABS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    role="tab"
                    aria-selected={p === type}
                    onClick={() => onSwitchProvider(p)}
                    className={`relative shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${p === type
                      ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                      }`}
                  >
                    <ProviderIcon provider={p} className="w-3.5 h-3.5" />
                    {PROVIDER_CONFIG[p].name.replace(/ \(.*\)$/, '')}
                    {configuredProviders[p] && (
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-label="configured" />
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Header with gradient and icon */}
            <div className={`relative bg-gradient-to-br ${providerConfig.bgGradient} px-6 py-6 border-b border-gray-100 dark:border-gray-800 overflow-hidden`}>
              {/* Decorative circles */}
              <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10 dark:bg-white/5" />
              <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full bg-white/10 dark:bg-white/5" />

              <div className="relative flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl ${providerConfig.iconBg} flex items-center justify-center shadow-lg`}>
                    <ProviderIcon provider={type} className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className={`text-xl font-bold ${providerConfig.brandColor}`}>
                      {providerConfig.name}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 max-w-[200px]">
                      {providerConfig.description}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 -m-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Urgency banner for missing key */}
              {!hasExistingKey && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-3 rounded-xl bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-400/30 dark:border-amber-500/20"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                        Unlock {providerConfig.name} Models
                      </p>
                      <p className="text-xs text-amber-700/80 dark:text-amber-300/70">
                        Paste a key to start chatting. It stays in this browser unless you turn on cloud sync.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Features list */}
            {!hasExistingKey && (
              <div className="px-6 py-4 bg-gray-50/50 dark:bg-gray-800/30 border-b border-gray-100 dark:border-gray-800">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                  What you will unlock
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {providerConfig.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <svg className={`w-4 h-4 shrink-0 ${providerConfig.brandColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Content */}
            <div className="px-6 py-5 space-y-4">
              {/* API Key Display/Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  API Key
                </label>
                {isEditMode ? (
                  <div className="relative">
                    <input
                      type="password"
                      autoComplete="off"
                      spellCheck={false}
                      value={apiKey}
                      onChange={handleApiKeyChange}
                      onKeyDown={(e) => { if (e.key === 'Enter' && apiKey.trim()) void handleSave(); }}
                      className="w-full px-4 py-3.5 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition-all font-mono text-sm"
                      placeholder={providerConfig.placeholder}
                      autoFocus
                    />
                    {apiKey && (
                      <button
                        onClick={() => setApiKey('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="w-full px-4 py-3.5 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 font-mono text-sm text-gray-600 dark:text-gray-300">
                    {hasExistingKey ? maskApiKey(apiKey) : (
                      <span className="text-gray-400">No API key configured</span>
                    )}
                  </div>
                )}
              </div>

              {/* Cloud sync option */}
              {user?.id && isEditMode && (
                <div className={`p-4 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50/50 dark:from-blue-900/40 dark:to-indigo-900/20 border border-blue-100 dark:border-blue-900/50 ${!isAllowCloud.allowApiKeyStorage ? 'opacity-60' : ''}`}>
                  <Tooltip message={isAllowCloud.allowApiKeyStorage ? "Sync your API key to access it across devices and in the Chrome extension" : isAllowCloud.message}>
                    <div className={!isAllowCloud.allowApiKeyStorage ? 'cursor-not-allowed' : ''}>
                      <SwitchWithLabel
                        value={isAllowCloud.allowApiKeyStorage ? isAddApiKeyCloud : false}
                        onChange={() => {
                          if (!isAllowCloud.allowApiKeyStorage) {
                            toast.error(isAllowCloud.message);
                            setUpgradeModal({ isOpen: true, lastOpen: new Date() });
                            return;
                          }
                          setIsAddApiKeyCloud(prev => !prev);
                        }}
                        size='small'
                        textClassName='text-sm text-gray-700 dark:text-gray-300'
                        label='Sync to cloud for cross-device access'
                      />
                    </div>
                  </Tooltip>
                  {!isAllowCloud.allowApiKeyStorage && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 ml-10">
                      {isAllowCloud.message}
                    </p>
                  )}
                  {isAllowCloud.allowApiKeyStorage && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 ml-10">
                      Access your key on any device and in our Chrome extension
                    </p>
                  )}
                </div>
              )}

              {/* Warning for cloud removal */}
              {apiKeyExist && !isAddApiKeyCloud && isEditMode && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800">
                  <svg className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Your cloud-synced API key will be removed when you save.
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="space-y-3 pt-2">
                {isEditMode ? (
                  <Button
                    loading={!!isLoadingAPIKey || isLoadingAdd || isLoadingRemove}
                    disabled={!apiKey.trim()}
                    className={`w-full py-3.5 px-4 rounded-xl font-semibold transition-all ${apiKey.trim()
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                      }`}
                    onClick={() => void handleSave()}
                  >
                    {hasExistingKey ? 'Update API Key' : 'Save & Unlock Models'}
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      className="flex-1 py-3 px-4 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium transition-colors"
                      onClick={() => setIsEditMode(true)}
                    >
                      Edit Key
                    </Button>
                    {isHasCloudAPI && (
                      <Tooltip message="Load API key from cloud storage">
                        <Button
                          className="py-3 px-4 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium transition-colors"
                          onClick={() => setIsLoadAPIKey(true)}
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                          </svg>
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                )}

                {/* Remove key option */}
                {hasExistingKey && !isEditMode && (
                  <>
                    {showConfirmRemove ? (
                      <div className="flex gap-2">
                        <Button
                          className="flex-1 py-2.5 px-4 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium transition-colors"
                          onClick={() => setShowConfirmRemove(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          loading={isLoadingRemove}
                          className="flex-1 py-2.5 px-4 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors shadow-lg shadow-red-500/25"
                          onClick={() => void handleRemove()}
                        >
                          Confirm Remove
                        </Button>
                      </div>
                    ) : (
                      <button
                        className="w-full py-2 text-sm text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 font-medium transition-colors"
                        onClick={() => setShowConfirmRemove(true)}
                      >
                        Remove API Key
                      </button>
                    )}
                  </>
                )}

                {/* Get API key link */}
                <a
                  href={providerConfig.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors group"
                >
                  <span>Get your {providerConfig.name} API key</span>
                  <svg className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

